import { DocumentNode, gql, useMutation } from '@apollo/client';
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react';
import { EditorState, convertFromRaw, convertToRaw } from 'draft-js';
import * as React from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { AnswerForm } from '@/components/AnswerForm';
import { RawDraftContentStateFragmentAST } from '@/fragments/RawDraftContentStateFragment';
import { logError } from '@/imports/logging/ClientLogger';
import { content_edited } from '@/analytics';
import { minutesSince } from '@/analytics/contentAnalysis';

import { EditAnswerModal_PostFragment_AnswerV2 } from './graphql-types/EditAnswerModal_PostFragment';
import {
  EditAnswerModal_UpdateAnswer,
  EditAnswerModal_UpdateAnswerVariables,
} from './graphql-types/EditAnswerModal_UpdateAnswer';

import { EditPostModalFooter } from '../EditPostModalFooter';

const POST_FRAGMENT = gql`
  fragment EditAnswerModal_PostFragment on PostV2 {
    _id
    dateCreated
    group {
      _id
    }
    organization {
      _id
    }
    sponsorData {
      isSponsoredPost
    }
    tags {
      _id
      name
    }
    embeddedImagesV2 {
      imageId
      imageProxy(input: { fit: "crop", ar: "4:3", h: 384 })
    }
    ... on AnswerV2 {
      answerBody: body
      answerBodyContentState: bodyContentState {
        ...RawDraftContentStateFragment
      }
    }
    versionFiles {
      _id
      file {
        _id
        name
        isVideo
        isImage
        url
      }
    }
  }
  ${RawDraftContentStateFragmentAST}
`;

const UPDATE_ANSWER = gql`
  mutation EditAnswerModal_UpdateAnswer($input: UpdateAnswerInput_V2!) {
    updateAnswerV2(input: $input) {
      data {
        _id
        ...EditAnswerModal_PostFragment
      }
    }
  }
  ${POST_FRAGMENT}
`;

type AnswerFormValue = {
  images: ({ imageId: string } | { videoId: string })[];
  body: EditorState;
  tags?: { _id: string; name: string }[];
  attachments: { versionFileId: string; fileName: string }[];
};

export const EditAnswerModal: React.FC<{
  post: EditAnswerModal_PostFragment_AnswerV2;
  isOpen: boolean;
  onClose: () => void;
}> & { postFragment: DocumentNode } = ({ post, isOpen, onClose }) => {
  const [updateAnswer] = useMutation<
    EditAnswerModal_UpdateAnswer,
    EditAnswerModal_UpdateAnswerVariables
  >(UPDATE_ANSWER);

  const toast = useToast();

  const methods = useForm<AnswerFormValue>({
    defaultValues: {
      images: post.embeddedImagesV2,
      body: post?.answerBody
        ? EditorState.createWithContent(convertFromRaw(post?.answerBody))
        : EditorState.createEmpty(),
      tags: post.tags || [],
      attachments: post.versionFiles.map((files) => {
        return {
          versionFileId: files._id,
          fileName: files.file?.name || 'cant-load.file',
        };
      }),
    },
  });

  const { handleSubmit, formState } = methods;

  const submit = handleSubmit(async (params) => {
    try {
      await updateAnswer({
        variables: {
          input: {
            answerPostId: post._id,
            data: {
              body: convertToRaw(params.body.getCurrentContent()),
              versionFileIds: params.attachments.map(
                (file) => file.versionFileId
              ),
              tagIds: params.tags?.map((tag) => tag._id),
              embeddedImageIds: params.images
                .filter((id): id is { imageId: string } => 'imageId' in id)
                .map((id) => id.imageId),
            },
          },
        },
      });

      // Track content edit
      content_edited({
        entity: {
          id: post._id,
          type: 'post',
        },
        content: {
          contentType: 'answer',
        },
        timing: {
          timeSinceCreation: (post as any).dateCreated ? minutesSince((post as any).dateCreated) : 0,
        },
      });

      onClose();
    } catch (e) {
      toast({
        status: 'error',
        description: 'Failed to update answer.',
      });
      logError(e);
    }
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" closeOnEsc={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader textAlign="center">Edit Answer</ModalHeader>
        <ModalCloseButton />

        <ModalBody px="6" py="4">
          <FormProvider {...methods}>
            <AnswerForm
              organizationId={post.organization?._id}
              groupId={post.group?._id}
              originalImages={post.embeddedImagesV2.filter(
                (
                  image
                ): image is {
                  imageId: string;
                  imageProxy: string;
                  __typename: 'PostEmbeddedImage';
                } => {
                  return !!image.imageProxy;
                }
              )}
              editing
            />
          </FormProvider>
        </ModalBody>
        <EditPostModalFooter>
          <Box mt={[2, 0]} display="flex" flexDir={['column', 'row-reverse']}>
            <Button
              isDisabled={formState.isSubmitted && !formState.isValid}
              onClick={submit}
            >
              Post
            </Button>
          </Box>
        </EditPostModalFooter>
      </ModalContent>
    </Modal>
  );
};
EditAnswerModal.postFragment = POST_FRAGMENT;
